import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { ChevronLeft } from 'lucide-react'
import { COMPONENT_CATEGORIES, DATA_REQUIREMENTS } from '@/types/admin'
import type { FieldDef } from '@/types/admin'

function JsonField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  rows = 4,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  rows?: number
}) {
  const [error, setError] = useState<string | null>(null)

  function handleChange(v: string) {
    onChange(v)
    if (!v.trim()) {
      setError(null)
      return
    }
    try {
      JSON.parse(v)
      setError(null)
    } catch {
      setError('Format JSON tidak valid')
    }
  }

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <textarea
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export default function CreateComponentPage() {
  const navigate = useNavigate()

  const [form, setForm] = useState({
    component_key: '',
    label: '',
    category: COMPONENT_CATEGORIES[0] as string,
    description: '',
    preview: '',
    data_requirement: '' as string,
    sort_order: '0',
    is_active: true,
    default_settings: '{}',
    settings_schema: '[]',
  })

  const set = (field: keyof typeof form, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const create = useMutation({
    mutationFn: () => {
      let default_settings: Record<string, unknown> = {}
      let settings_schema: FieldDef[] = []
      try {
        default_settings = JSON.parse(form.default_settings || '{}')
      } catch { /* invalid JSON — caught by field component */ }
      try {
        settings_schema = JSON.parse(form.settings_schema || '[]') as FieldDef[]
      } catch { /* invalid JSON */ }

      return adminApi.createComponent({
        component_key: form.component_key.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        category: form.category,
        label: form.label,
        description: form.description || undefined,
        preview: form.preview || undefined,
        data_requirement: form.data_requirement || null,
        sort_order: parseInt(form.sort_order, 10) || 0,
        is_active: form.is_active,
        default_settings,
        settings_schema,
      })
    },
    onSuccess: (data) => {
      navigate(`/components/${data.component_id}`)
    },
  })

  function isFormValid() {
    if (!form.component_key || !form.label || !form.category) return false
    if (!/^[a-z][a-z0-9-]*$/.test(form.component_key)) return false
    try { JSON.parse(form.default_settings || '{}') } catch { return false }
    try { JSON.parse(form.settings_schema || '[]') } catch { return false }
    return true
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isFormValid()) return
    create.mutate()
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* Back */}
      <Link
        to="/components"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Komponen
      </Link>

      <div>
        <h1 className="text-2xl font-bold">Tambah Komponen</h1>
        <p className="text-sm text-muted-foreground">
          Daftarkan komponen baru ke katalog. Developer perlu mengimplementasi React component
          yang sesuai di oneidol-public.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* component_key */}
        <div className="space-y-1">
          <label className="text-sm font-medium">
            Component Key <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={form.component_key}
            onChange={(e) => set('component_key', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="hero-banner"
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
          />
          {form.component_key && !/^[a-z][a-z0-9-]*$/.test(form.component_key) && (
            <p className="text-xs text-destructive">
              Harus diawali huruf kecil, hanya boleh huruf kecil, angka, dan tanda hubung
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Harus sesuai dengan nama React component di oneidol-public (tidak bisa diubah setelah disimpan)
          </p>
        </div>

        {/* label */}
        <div className="space-y-1">
          <label className="text-sm font-medium">
            Label <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="Banner Penuh"
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* category */}
        <div className="space-y-1">
          <label className="text-sm font-medium">
            Kategori <span className="text-destructive">*</span>
          </label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {COMPONENT_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* description */}
        <div className="space-y-1">
          <label className="text-sm font-medium">Deskripsi</label>
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            placeholder="Gambar banner full-width dengan overlay teks dan tombol CTA."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
          />
        </div>

        {/* preview */}
        <div className="space-y-1">
          <label className="text-sm font-medium">URL Preview (gambar / GIF)</label>
          <input
            type="text"
            value={form.preview}
            onChange={(e) => set('preview', e.target.value)}
            placeholder="https://example.com/preview.gif"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {form.preview && (
            <div className="mt-2 rounded-lg border border-border overflow-hidden h-32 bg-muted/30">
              <img src={form.preview} alt="preview" className="w-full h-full object-cover" />
            </div>
          )}
          <p className="text-xs text-muted-foreground">URL gambar atau GIF yang ditampilkan di galeri komponen tenant.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* data_requirement */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Data Requirement</label>
            <select
              value={form.data_requirement}
              onChange={(e) => set('data_requirement', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Tidak ada</option>
              {DATA_REQUIREMENTS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* sort_order */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Urutan</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => set('sort_order', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* is_active */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          <label htmlFor="is_active" className="text-sm font-medium">Aktif</label>
          <span className="text-xs text-muted-foreground">(Komponen tidak aktif tidak muncul di tenant dashboard)</span>
        </div>

        {/* default_settings */}
        <JsonField
          label="Default Settings"
          value={form.default_settings}
          onChange={(v) => set('default_settings', v)}
          placeholder={'{"cta_label":"Cheki Sekarang","cta_href":"/cheki"}'}
          hint='JSON object. Nilai default saat tenant menambahkan komponen ini.'
          rows={4}
        />

        {/* settings_schema */}
        <JsonField
          label="Settings Schema"
          value={form.settings_schema}
          onChange={(v) => set('settings_schema', v)}
          placeholder={'[{"key":"cta_label","label":"Label Tombol","type":"text"}]'}
          hint='JSON array of FieldDef. Menentukan field yang muncul di settings panel tenant.'
          rows={6}
        />

        {create.error && (
          <p className="text-sm text-destructive">
            {(create.error as Error).message ?? 'Terjadi kesalahan.'}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={create.isPending || !isFormValid()}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {create.isPending ? 'Menyimpan...' : 'Simpan'}
          </button>
          <Link
            to="/components"
            className="rounded-md border border-border px-6 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            Batal
          </Link>
        </div>
      </form>
    </div>
  )
}
